// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import { Toaster } from "react-hot-toast";

// Components
import WorkerLayout from "./components/WorkerLayout";
import ScrollToTop from "./components/ScrollToTop";
// Standard Pages
import WorkerSignup from "./pages/WorkerSignup";
import WorkerLogin from "./pages/WorkerLogin";
import SelectServices from "./pages/SelectServices";
import LegalName from "./pages/LegalName";
import UploadDocuments from "./pages/UploadDocuments";
import WorkerDashboard from "./pages/WorkerDashboard";
import VerifyPhone from "./pages/VerifyPhone";
import OTPVerification from "./pages/OTPVerification";
import SetAddress from "./pages/SetAddress";
import YourRiders from "./pages/YourRiders";
import AppliedJobs from "./pages/AppliedJobs";
import MyJobs from "./pages/MyJobs";
import CustomerReview from "./pages/CustomerReview";
import JobCompleted from "./pages/JobCompleted";
import AdminDashboard from "./pages/AdminDashboard";
import WorkerProfile from "./pages/WorkerProfile";
import JobDetail from "./pages/JobDetail";
import Tier from "./pages/Tier";
import ReviewProfile from "./pages/ReviewProfile";
import Settings from "./pages/Settings";
import WorkerDocuments from "./pages/WorkerDocuments";
import ProfilePhotoUpload from "./pages/ProfilePhotoUpload";
import BackgroundCheckStatus from "./pages/BackgroundCheckStatus";
import WorkCertifications from "./pages/WorkCertifications";
import WorkerTerms from "./pages/WorkerTerms";
import WorkerPublicProfile from "./pages/WorkerPublicProfile";
import ChatPage from "./pages/ChatPage";
import Inbox from "./pages/Inbox";
import Opportunities from "./pages/Opportunities";
import RideSuccess from "./pages/RideSuccess";
import EditRide from "./ride/EditRide";
import RideRequests from "./ride/RideRequests";
import InvitePassengers from "./ride/InvitePassengers";
import WorkerRideChatPage from "./pages/WorkerRideChatPage";
import RideDashboard from "./ride/RideDashboard";
import ServiceUpload from "./pages/ServiceUpload";
import RideRequestsHub from "./ride/RideRequestsHub";
import RideDriverSetup from "./pages/RideDriverSetup";
import RideDocuments from "./pages/RideDocuments";
import IntroAnimation from "./pages/IntroAnimation";
import Payments from "./pages/Payments";
import ReuploadId from "./pages/ReuploadId";
import StripeSuccess from "./pages/StripeSuccess";
import StripeRefresh from "./pages/StripeRefresh";
import Jobs from "./pages/Jobs";
import "./pages/IntroAnimation.css";
import WorkerSupportChat from "./pages/WorkerSupportChat";
import WorkerHelp from "./pages/WorkerHelp";
import WorkerForgotPassword from "./pages/WorkerForgotPassword";
import WorkerResetPassword from "./pages/WorkerResetPassword";
import StripeGuide from "./pages/StripeGuide";
// ✅ ProtectedRoute
const ProtectedRoute = ({ element: Component, allowedStatuses }) => {
  const workerId = localStorage.getItem("workerId");
  const status = localStorage.getItem("workerStatus");

  if (!workerId) return <Navigate to="/worker-login" replace />;
  if (status && allowedStatuses.includes(status)) return <Component />;
  return <Navigate to="/worker-login" replace />;
};

const App = () => {
  const [redirectPath, setRedirectPath] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showAnimation, setShowAnimation] = useState(
    !localStorage.getItem("animationShown")
  );

  // ✅ Theme setup
  useEffect(() => {
    const saved = localStorage.getItem("theme") || "system";
    const root = document.documentElement;
    if (
      saved === "dark" ||
      (saved === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, []);

  // ✅ Intro Animation
  useEffect(() => {
    if (showAnimation) {
      const timer = setTimeout(() => {
        setShowAnimation(false);
        localStorage.setItem("animationShown", "true");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showAnimation]);

  // ✅ Status Check
  useEffect(() => {
    const checkWorkerStatus = async () => {
      const workerId = localStorage.getItem("workerId");
      if (!workerId) {
        setRedirectPath("/worker-login");
        setCheckingStatus(false);
        return;
      }

      try {
        const API_WRK = process.env.REACT_APP_API_WRK;
        const res = await axios.get(`${API_WRK}/profile/${workerId}`);
        const worker = res.data;

        const safeStatus = worker?.status || "incomplete";
        localStorage.setItem("workerStatus", safeStatus);

        const hasName = worker.name && worker.name.trim().length > 1;
        const hasPhone = !!worker.phone;
        const hasAddress = !!worker.address;

        const hasDocs = Boolean(
          worker.id1FrontUrl &&
            worker.id1BackUrl &&
            worker.id2FrontUrl &&
            worker.id2BackUrl &&
            worker.selfieUrl &&
            (!worker.isInternational || worker.permitUrl)
        );

        const hasServices = worker.services && worker.services.length > 0;

        if (!hasName) setRedirectPath("/legal-name");
        else if (!hasPhone) setRedirectPath("/verify-phone");
        else if (!hasAddress) setRedirectPath("/set-address");
        else if (!hasDocs) setRedirectPath("/upload-documents");
        else if (!hasServices) setRedirectPath("/select-services");
        else if (safeStatus === "incomplete") setRedirectPath("/review-profile");
        else if (["pending", "approved"].includes(safeStatus))
          setRedirectPath("/dashboard");
        else setRedirectPath("/worker-login");
      } catch (err) {
        console.error("❌ Error checking worker status:", err);
        setRedirectPath("/worker-login");
      } finally {
        setCheckingStatus(false);
      }
    };

    checkWorkerStatus();
  }, []);

  return (
    <Router>
      <ScrollToTop />
  <Toaster position="top-center" reverseOrder={false} />

      {showAnimation ? (
        <IntroAnimation />
      ) : checkingStatus ? (
        <div className="p-6 text-center text-gray-600">Loading...</div>
      ) : (
        <Routes>
          {/* Redirect root */}
          <Route
            path="/"
            element={
              redirectPath ? (
                <Navigate to={redirectPath} replace />
              ) : (
                <div className="p-6 text-center">Loading...</div>
              )
            }
          />

          {/* Authentication & Onboarding (no layout) */}

          <Route path="/worker-login" element={<WorkerLogin />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/worker-signup" element={<WorkerSignup />} />
          <Route path="/legal-name" element={<LegalName />} />
          <Route path="/verify-phone" element={<VerifyPhone />} />
          <Route path="/otp-verification" element={<OTPVerification />} />
          <Route path="/set-address" element={<SetAddress />} />
          <Route path="/upload-documents" element={<UploadDocuments />} />
          <Route path="/select-services" element={<SelectServices />} />
          <Route
            path="/review-profile"
            element={
              <ProtectedRoute
                element={ReviewProfile}
                allowedStatuses={["incomplete"]}
              />
            }
          />

         <Route path="/ride/requests" element={<RideRequestsHub />} />
          <Route
  path="/upload-profile-photo"
  element={
    <ProtectedRoute
      element={ProfilePhotoUpload}
      allowedStatuses={["pending", "incomplete", "approved"]}
    />
  }
/>

<Route
  path="/ride-documents"
  element={
    <ProtectedRoute
      element={RideDocuments}
      allowedStatuses={["pending", "incomplete", "approved"]}
    />
  }
/>

          <Route path="/worker/stripe-guide" element={<StripeGuide />} />
          <Route path="/worker-forgot-password" element={<WorkerForgotPassword />} />
<Route path="/worker-reset-password" element={<WorkerResetPassword />} />

          {/* ✅ Jobs Routes */}
          <Route path="/jobs" element={<Navigate to="/my-jobs" replace />} />
          <Route
            path="/my-jobs"
            element={
              <WorkerLayout>
                <MyJobs />
              </WorkerLayout>
            }
          />
          <Route
            path="/applied-jobs"
            element={
              <WorkerLayout>
                <AppliedJobs />
              </WorkerLayout>
            }
          />
           <Route
            path="/worker-live-chat"
            element={
              <WorkerLayout>
                <WorkerSupportChat />
              </WorkerLayout>
            }
          />

          {/* Dashboard & Other Worker Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                element={() => (
                  <WorkerLayout>
                    <WorkerDashboard />
                  </WorkerLayout>
                )}
                allowedStatuses={["pending", "approved"]}
              />
            }
          />

          <Route
            path="/worker-profile"
            element={
              <WorkerLayout>
                <WorkerProfile />
              </WorkerLayout>
            }
          />
          <Route
            path="/tier"
            element={
              <WorkerLayout>
                <Tier />
              </WorkerLayout>
            }
          />
          <Route
            path="/documents"
            element={
              <WorkerLayout>
                <WorkerDocuments />
              </WorkerLayout>
            }
          />
          <Route
            path="/payments"
            element={
              <WorkerLayout>
                <Payments />
              </WorkerLayout>
            }
          />
          <Route
            path="/settings"
            element={
              <WorkerLayout>
                <Settings />
              </WorkerLayout>
            }
          />
          <Route
            path="/opportunities"
            element={
              <WorkerLayout>
                <Opportunities />
              </WorkerLayout>
            }
          />
          <Route
            path="/inbox"
            element={
              <WorkerLayout>
                <Inbox />
              </WorkerLayout>
            }
          />
          <Route
            path="/job/:jobId"
            element={
              <WorkerLayout>
                <JobDetail />
              </WorkerLayout>
            }
          />
          <Route
            path="/chat/:sender/:receiver/:jobId"
            element={
              <WorkerLayout>
                <ChatPage />
              </WorkerLayout>
            }
          />

          {/* Rides Section */}
          <Route
            path="/ride/*"
            element={
              <WorkerLayout>
                <RideDashboard />
              </WorkerLayout>
            }
          />
          <Route
            path="/ride-success"
            element={
              <WorkerLayout>
                <RideSuccess />
              </WorkerLayout>
            }
          />
           <Route
            path="/ride/riders/:rideId"
            element={
              <WorkerLayout>
                <YourRiders />
              </WorkerLayout>
            }
          />
          <Route
            path="/ride/edit/:rideId"
            element={
              <WorkerLayout>
                <EditRide />
              </WorkerLayout>
            }
          />
          <Route
            path="/ride/requests/:rideId"
            element={
              <WorkerLayout>
                <RideRequests />
              </WorkerLayout>
            }
          />
          <Route
            path="/ride/invite/:rideId"
            element={
              <WorkerLayout>
                <InvitePassengers />
              </WorkerLayout>
            }
          />
          <Route
            path="/worker/ride-chat/:rideId/:customerId"
            element={
              <WorkerLayout>
                <WorkerRideChatPage />
              </WorkerLayout>
            }
          />

          {/* Misc */}
          <Route
            path="/public-profile"
            element={
              <WorkerLayout>
                <WorkerPublicProfile />
              </WorkerLayout>
            }
          />
          <Route
            path="/background-check"
            element={
              <WorkerLayout>
                <BackgroundCheckStatus />
              </WorkerLayout>
            }
          />
          <Route
            path="/work-certifications"
            element={
              <WorkerLayout>
                <WorkCertifications />
              </WorkerLayout>
            }
          />
          <Route
            path="/terms"
            element={
              <WorkerLayout>
                <WorkerTerms />
              </WorkerLayout>
            }
          />
          <Route
  path="/help"
  element={
    <WorkerLayout>
      <WorkerHelp />
    </WorkerLayout>
  }
/>
          <Route
            path="/service-upload"
            element={
              <WorkerLayout>
                <ServiceUpload />
              </WorkerLayout>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <WorkerLayout>
                <AdminDashboard />
              </WorkerLayout>
            }
          />
          <Route
            path="/worker/stripe-success"
            element={
              <WorkerLayout>
                <StripeSuccess />
              </WorkerLayout>
            }
          />
          <Route
            path="/worker/stripe-refresh"
            element={
              <WorkerLayout>
                <StripeRefresh />
              </WorkerLayout>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <h1 className="text-center text-2xl mt-10">Page Not Found</h1>
            }
          />
        </Routes>
      )}
    </Router>
  );
};

export default App;
